import { createHash } from 'node:crypto'
import { Timestamp } from '@klear/time'
import type { SignerData } from '@klear/trader-legal-agreement/trader-legal-agreement-acceptance.wire'

export type TraderLegalAgreementAcceptanceIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type TraderLegalAgreementAcceptanceJson = {
  identity: TraderLegalAgreementAcceptanceIdentity
  legalAgreementId: number
  traderId: number
  userId: number
  ipAddress: string
  signature?: string
  documentSignatureUrl?: string
  documentHash?: string
  signedPdfHash?: string
  documentContentUrl?: string
  signerData?: SignerData
  documentPresentedAt?: number | Timestamp
  documentSignedAt?: number | Timestamp
  userAgent?: string
  consentText?: string
  consentVersionId?: string
  consentEvent?: string
  signingSessionId?: number
  createdAt?: number | Timestamp
}

export function traderLegalAgreementAcceptanceToJson(acceptance: TraderLegalAgreementAcceptance) {
  return {
    identity: acceptance.identity,
    legalAgreementId: acceptance.legalAgreementId,
    traderId: acceptance.traderId,
    userId: acceptance.userId,
    ipAddress: acceptance.ipAddress,
    signature: acceptance.signature,
    documentSignatureUrl: acceptance.documentSignatureUrl,
    documentHash: acceptance.documentHash,
    signedPdfHash: acceptance.signedPdfHash,
    documentContentUrl: acceptance.documentContentUrl,
    signerData: acceptance.signerData,
    documentPresentedAt: acceptance.documentPresentedAt?.epochMs,
    documentSignedAt: acceptance.documentSignedAt?.epochMs,
    userAgent: acceptance.userAgent,
    consentText: acceptance.consentText,
    consentVersionId: acceptance.consentVersionId,
    consentEvent: acceptance.consentEvent,
    signingSessionId: acceptance.signingSessionId,
    createdAt: acceptance.createdAt?.epochMs
  }
}

export class TraderLegalAgreementAcceptance {
  static readonly __klearClass = 'TraderLegalAgreementAcceptance'

  readonly identity: TraderLegalAgreementAcceptanceIdentity
  legalAgreementId: number
  traderId: number
  userId: number
  ipAddress: string
  signature?: string
  documentSignatureUrl?: string
  documentHash?: string
  signedPdfHash?: string
  documentContentUrl?: string
  signerData?: SignerData
  documentPresentedAt?: Timestamp
  documentSignedAt?: Timestamp
  userAgent?: string
  consentText?: string
  consentVersionId?: string
  consentEvent?: string
  signingSessionId?: number
  createdAt?: Timestamp

  constructor(props: TraderLegalAgreementAcceptanceJson) {
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.legalAgreementId = props.legalAgreementId
    this.traderId = props.traderId
    this.userId = props.userId
    this.ipAddress = props.ipAddress
    this.signature = props.signature
    this.documentSignatureUrl = props.documentSignatureUrl
    this.documentHash = props.documentHash
    this.signedPdfHash = props.signedPdfHash
    this.documentContentUrl = props.documentContentUrl
    this.signerData = props.signerData
    this.documentPresentedAt = props.documentPresentedAt != null ? new Timestamp(props.documentPresentedAt) : undefined
    this.documentSignedAt = props.documentSignedAt != null ? new Timestamp(props.documentSignedAt) : undefined
    this.userAgent = props.userAgent
    this.consentText = props.consentText
    this.consentVersionId = props.consentVersionId
    this.consentEvent = props.consentEvent
    this.signingSessionId = props.signingSessionId
    this.createdAt = props.createdAt != null ? new Timestamp(props.createdAt) : undefined
    this.assertValid()
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get signerFirstName(): string {
    return this.signerData?.firstName ?? ''
  }

  get signerLastName(): string {
    return this.signerData?.lastName ?? ''
  }

  get signerEmail(): string {
    return this.signerData?.email ?? ''
  }

  get signerSignature(): string | undefined {
    return this.signature
  }

  assertValid(): void {
    if (!this.legalAgreementId) {
      throw new Error('TraderLegalAgreementAcceptance legalAgreementId is required')
    }
    if (!this.traderId) {
      throw new Error('TraderLegalAgreementAcceptance traderId is required')
    }
  }

  static computeDocumentHash(htmlContent: string): string {
    return createHash('sha256').update(htmlContent).digest('hex')
  }

  verifyDocumentHash(htmlContent: string): boolean {
    if (!this.documentHash) {
      return false
    }
    return TraderLegalAgreementAcceptance.computeDocumentHash(htmlContent) === this.documentHash
  }

  toJSON() {
    return traderLegalAgreementAcceptanceToJson(this)
  }
}
